import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { SnackbarProvider } from 'notistack';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/en-au';
import { DateProvider } from './context/DateContext';
import ErrorBoundary from './components/ErrorBoundary';
import DashboardLayout from './layouts/DashboardLayout';
import Dashboard from './pages/UnifiedDashboard';
import Clients from './pages/Clients';
import ClientDetails from './pages/ClientDetails';
import Locations from './pages/Locations';
import LocationDetails from './pages/LocationDetails';
import Insights from './pages/Insights';
import PurePromote from './pages/PurePromote';
import Marketing from './pages/Marketing';
import Outreach from './pages/Outreach';
import Registrations from './pages/Registrations';
import ClientWatchlist from './pages/ClientWatchlist';
import Onboarding from './pages/Onboarding';
import CeoReportingDashboard from './pages/CeoReportingDashboard';
import CeoMetricDetailPage from './pages/CeoMetricDetailPage';
import UserManagement from './pages/UserManagement';
import { ProtectedRoute, useDefaultRoute } from './components/ProtectedRoute';
import { ROUTE_KEYS } from './utils/roles';
import './index.css';

dayjs.locale('en-au');

function DefaultHomeRedirect() {
  const defaultRoute = useDefaultRoute();
  return <Navigate to={defaultRoute} replace />;
}

function App({ onLogout, userRole }) {
  return (
    <ErrorBoundary>
      <DateProvider>
        <LocalizationProvider
          dateAdapter={AdapterDayjs}
          adapterLocale="en-au"
          localeText={{
            start: 'Start Date',
            end: 'End Date',
            fieldMonthPlaceholder: () => 'MM',
            fieldDayPlaceholder: () => 'DD',
            fieldYearPlaceholder: () => 'YYYY',
          }}
        >
          <SnackbarProvider maxSnack={3} autoHideDuration={3000}>
            <Router>
              <ErrorBoundary>
                <Routes>
                  <Route path="/" element={<DashboardLayout onLogout={onLogout} userRole={userRole} />}>
                    <Route index element={<DefaultHomeRedirect />} />

                    <Route path="ceo" element={<Navigate to="/dashboard" replace />} />
                    <Route
                      path="ceo/detail/:metric"
                      element={
                        <ProtectedRoute routeKey={ROUTE_KEYS.CEO}>
                          <CeoMetricDetailPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="dashboard"
                      element={
                        <ProtectedRoute routeKey={ROUTE_KEYS.DASHBOARD}>
                          <Dashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="clients"
                      element={
                        <ProtectedRoute routeKey={ROUTE_KEYS.CLIENTS}>
                          <Clients />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="clients/:clientId"
                      element={
                        <ProtectedRoute routeKey={ROUTE_KEYS.CLIENTS}>
                          <ClientDetails />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="locations"
                      element={
                        <ProtectedRoute routeKey={ROUTE_KEYS.LOCATIONS}>
                          <Locations />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="locations/:locationId"
                      element={
                        <ProtectedRoute routeKey={ROUTE_KEYS.LOCATIONS}>
                          <LocationDetails />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="insights"
                      element={
                        <ProtectedRoute routeKey={ROUTE_KEYS.INSIGHTS}>
                          <Insights />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="pure-promote"
                      element={
                        <ProtectedRoute routeKey={ROUTE_KEYS.PURE_PROMOTE}>
                          <PurePromote />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="marketing"
                      element={
                        <ProtectedRoute routeKey={ROUTE_KEYS.MARKETING}>
                          <Marketing />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="onboarding"
                      element={
                        <ProtectedRoute routeKey={ROUTE_KEYS.ONBOARDING}>
                          <Onboarding />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="outreach"
                      element={
                        <ProtectedRoute routeKey={ROUTE_KEYS.OUTREACH}>
                          <Outreach />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="registrations"
                      element={
                        <ProtectedRoute routeKey={ROUTE_KEYS.REGISTRATIONS}>
                          <Registrations />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="client-watchlist"
                      element={
                        <ProtectedRoute routeKey={ROUTE_KEYS.CLIENT_WATCHLIST}>
                          <ClientWatchlist />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="users"
                      element={
                        <ProtectedRoute routeKey={ROUTE_KEYS.USER_MANAGEMENT}>
                          <UserManagement />
                        </ProtectedRoute>
                      }
                    />

                    <Route path="*" element={<DefaultHomeRedirect />} />
                  </Route>
                </Routes>
              </ErrorBoundary>
            </Router>
          </SnackbarProvider>
        </LocalizationProvider>
      </DateProvider>
    </ErrorBoundary>
  );
}

export default App;
